import { __assign } from 'tslib';
import * as React from 'react';
import { classNames, useUitkContext } from 'uitk-react-utils';
import styles from './uitk-carousel.css';
var getClassNameList = classNames.bind(styles);
var UitkCarouselItem = function (props) {
  var children = props.children,
    wrapItems = props.wrapItems,
    ariaHidden = props.ariaHidden,
    ariaLabel = props.ariaLabel,
    stretchToFill = props.stretchToFill,
    carouselContainerRef = props.carouselContainerRef,
    itemIndex = props.itemIndex;
  var _a = React.useState(false),
    isNaturalWidthItemVisible = _a[0],
    setIsNaturalWidthItemVisible = _a[1];
  var itemRef = React.useRef(null);
  var contextData = useUitkContext().data;
  var _b = contextData !== null && contextData !== void 0 ? contextData : {},
    carouselWindowWidth = _b.carouselWindowWidth,
    carouselSize = _b.carouselSize,
    isNaturalWidthItemsCarousel = _b.isNaturalWidthItemsCarousel,
    setSnapPointItem = _b.setSnapPointItem,
    setNaturalWidthItemsViewed = _b.setNaturalWidthItemsViewed,
    setNaturalWidthItemsInView = _b.setNaturalWidthItemsInView,
    isRtl = _b.isRtl,
    carouselIsVisible = _b.carouselIsVisible;
  var baseClassName = 'uitk-carousel-item';
  if (!stretchToFill) {
    baseClassName += ' uitk-carousel-item-size-auto';
  }
  var toggleAriaHidden = function (item) {
    var itemClassName = wrapItems ? item.props.className : '';
    var a11yHiddenAttrs = {
      'aria-hidden': true,
      tabIndex: -1
    };
    var hideItemFromScreenReaders = !isNaturalWidthItemsCarousel && ariaHidden || isNaturalWidthItemsCarousel && !isNaturalWidthItemVisible;
    var itemProps = __assign(__assign({}, hideItemFromScreenReaders ? a11yHiddenAttrs : null), {
      className: getClassNameList(itemClassName ? "".concat(itemClassName, " ").concat(baseClassName) : baseClassName),
      'data-stid': 'carousel-item',
      'aria-label': ariaLabel,
      ref: itemRef
    });
    // Hides inner focusable nodes of a carousel item from screen readers when out of view
    if (itemRef.current) {
      var allMatchingInnerNodes = itemRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      var arrayOfMatchingInnerNodes = Array.from(allMatchingInnerNodes);
      arrayOfMatchingInnerNodes.filter(function (elem) {
        return !elem.hasAttribute('aria-hidden');
      }).forEach(function (matchingNode) {
        matchingNode[hideItemFromScreenReaders ? 'setAttribute' : 'removeAttribute']('tabindex', '-1');
      });
    }
    if (!wrapItems) {
      return React.cloneElement(item, itemProps);
    }
    return React.createElement("div", __assign({}, itemProps), item);
  };
  var useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;
  useEnhancedEffect(function () {
    var _a, _b;
    if (isRtl) {
      // We can't rely on the offsetLeft value for RTL carousel, so are using the item's width instead
      var itemBoundingClientWidth = (_a = itemRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width;
      if (itemIndex !== undefined && itemBoundingClientWidth) {
        setSnapPointItem && setSnapPointItem({
          payload: {
            itemIndex: itemIndex,
            snapPoint: itemBoundingClientWidth
          }
        });
      }
    } else {
      var itemOffsetLeft = (_b = itemRef.current) === null || _b === void 0 ? void 0 : _b.offsetLeft;
      setSnapPointItem && setSnapPointItem({
        payload: {
          itemIndex: itemIndex,
          snapPoint: itemOffsetLeft
        }
      });
    }
  }, [carouselWindowWidth, carouselIsVisible, carouselSize, isRtl]);
  React.useEffect(function () {
    // Used to determine if the natural width item is visible and set a11y-related attributes
    var intersectionObserverOptions = {
      threshold: [0, 0.25, 0.5, 0.75, 1],
      root: carouselContainerRef === null || carouselContainerRef === void 0 ? void 0 : carouselContainerRef.current
    };
    /* istanbul ignore next */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var isFullyVisible = entry.intersectionRatio === 1;
        setIsNaturalWidthItemVisible(isFullyVisible);
        setNaturalWidthItemsInView({
          payload: {
            itemIndex: itemIndex,
            inView: isFullyVisible
          }
        });
        isFullyVisible && setNaturalWidthItemsViewed({
          payload: {
            itemIndex: itemIndex
          }
        });
      });
    }, intersectionObserverOptions);
    var itemRefCurrent = itemRef.current;
    isNaturalWidthItemsCarousel && itemRefCurrent && observer.observe(itemRefCurrent);
    return function () {
      itemRefCurrent && observer.unobserve(itemRefCurrent);
    };
  }, [carouselContainerRef, isNaturalWidthItemsCarousel, itemIndex, setNaturalWidthItemsViewed, setNaturalWidthItemsInView]);
  var renderCarouselItem = function () {
    var item = children;
    return toggleAriaHidden(item);
  };
  return renderCarouselItem();
};
UitkCarouselItem.displayName = 'UitkCarouselItem';
UitkCarouselItem.defaultProps = {
  wrapItems: true
};
export { UitkCarouselItem };